// import { Container as CommonContainer } from 'components';
import { SecondaryContainer as CommonContainer } from "components";
import { Pagination, NoContent, Loading } from "components";
import styled from "styled-components";
import { generateImage } from "utils/newFunction";
import { responsive } from "utils/theme";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import Image from "components/Image";
import { ApiService } from "services/client";
import { Formatter } from "utils/formatter";
import { toast } from "react-toastify";
import Toast from "components/Toast/Toast";
import {
  persianToGregorian,
  persianDateTimeToGregorian,
  getDifferenceDateTime,
  isWarningDateAfterCurrentDate,
} from "./tools";
import { useLocale } from "hooks";
import useCountdown from "hooks/useCountdown";

const SIZE = 6;

const NewsEventCard = ({ v, className, size = "small", single = false }) => {
  const { locale } = useLocale();

  const data = useMemo(() => {
    let label = "";
    const gregorianDate = v?.date?.value
      ? persianToGregorian(v?.date?.value, "YYYY-MM-DD", locale)
      : "";
    const gregorianWarningDate = v?.warningDate?.value
      ? persianDateTimeToGregorian(v?.warningDate?.value, "YYYY-MM-DD", locale)
      : "";
    const difference = getDifferenceDateTime(v?.date?.value);
    const splittedDifference = difference.split(":").map(Number);

    if (v?.label?.value) {
      label = v?.label?.value;
    } else if (isWarningDateAfterCurrentDate(v?.date?.value)) {
      label = locale === "fa" ? "پایان سررسید" : "Expired";
    } else if (splittedDifference[0] > 0) {
      label = `${
        splittedDifference[0] === 1
          ? locale === "fa"
            ? "یک"
            : "One"
          : splittedDifference[0]
      } ${
        locale === "fa"
          ? "روز تا سررسید"
          : `Day${splittedDifference[0] === 1 ? "" : "s"} left`
      }`;
    } else if (splittedDifference[0] === 0) {
      label = splittedDifference.slice(1).join(":");
    }

    return {
      id: v?.id,
      title:
        v?.["ResearchTitle"]?.value
          ?.split?.(" ")
          ?.map?.((item, i, arr) =>
            Number.isNaN(+item) ? (
              arr?.length === i + 1 ? (
                item
              ) : (
                `${item} `
              )
            ) : (
              <bdi className="ENGLISH_FONTS">{`${item} `}</bdi>
            )
          ) ?? "",
      image: generateImage(v?.["ResearchImage2"]?.value),
      // link: `/research-centers/${parentId}/${id}/${title}`
      link: v?.["Research_Internallink"]?.value || "#",
      alt: v?.imageTag?.value || v?.["ResearchTitle"]?.value || "",
      enableLabel:
        typeof v?.enableLabel?.value === "boolean" &&
        Boolean(v?.enableLabel?.value),
      color: isWarningDateAfterCurrentDate(v?.warningDate?.value)
        ? "#E71313" /*red*/
        : "#07BC0C" /*green*/,
      label,
      gregorianDate: gregorianDate?.toLocaleDateString?.("en-US") || "",
      gregorianWarningDate:
        gregorianWarningDate?.toLocaleDateString?.("en-US") || "",
    };
  }, [v]);

  const countDown = useCountdown(data?.label);

  return (
    <Link href={data.link} prefetch={false}>
      <a
        id={`card-${data?.id}`}
        className={clsx(
          "tw-flex tw-w-full tw-flex-col tw-group tw-m-0 tw-relative tw-overflow-hidden",
          className,
          {
            "sm:tw-w-1/2 tw-mx-auto": single,
            "lg:tw-w-2/3": size === "large",
            "lg:tw-w-[calc(100%/3)]": size !== "large",
          }
        )}
      >
        {data?.enableLabel && (
          <div
            style={{ rotate: "-45deg", background: data?.color }}
            dir={countDown.includes(":") ? "ltr" : "rtl"}
            className={clsx(
              "tw-text-center tw-absolute tw-z-50 tw-px-10 tw-text-[#FFFF] tw-text-base tw-font-bold tw-w-[90%] tw-left-[-30%] tw-top-[15%] tw-flex tw-items-center tw-justify-center",
              {
                "tw-h-[35px]": Boolean(countDown),
              }
            )}
          >
            {countDown?.replaceAll?.(":", " : ")}
          </div>
        )}
        <div
          className={clsx("tw-w-full", {
            "md:tw-aspect-[6/2]": size === "large",
          })}
        >
          <div
            className={clsx(
              "tw-relative tw-rounded-lg tw-h-full tw-overflow-hidden",
              {
                "tw-aspect-[3/2]": size !== "large",
                "tw-aspect-[6/2]": size === "large",
              }
            )}
          >
            <Image
              width={size === "small" ? 300 : 600}
              height={200}
              src={data?.image}
              alt={data?.alt}
              className={clsx(
                "tw-transition-all tw-rounded-lg xxxl:tw-rounded-[10.67px] tw-w-full tw-h-full tw-object-cover"
              )}
            />
            <div className="tw-rounded-lg tw-text-lg xxxl:tw-rounded-[10.67px] tw-font-medium tw-absolute tw-top-0 tw-w-full tw-h-full tw-flex tw-bg-gradient-to-b tw-items-end tw-pr-5 tw-pb-4 gradiant"></div>
          </div>
        </div>
        <span
          className={clsx(
            "tw-text-[#344456] tw-text-base xxxl:tw-text-[21.33px] tw-font-bold tw-mt-4 group-hover:tw-text-[#007FAC] tw-transition-all tw-min-h-[50px] xxxl:tw-mt-5 xxxl:tw-leading-[36.17px] title"
          )}
        >
          {data?.title}
        </span>
      </a>
    </Link>
  );
};

const StyledNewsEventCard = styled(NewsEventCard)`
  .title {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .gradiant {
    background: linear-gradient(
      180deg,
      rgba(52, 68, 86, 0) 66.67%,
      rgba(52, 68, 86, 0.7) 100%
    );
  }
  img {
    transition: transform 0.3s;
    @media (max-width: ${responsive.md}px) {
      transform: scaleY(1.06);
    }
  }

  &:hover {
    img {
      transform: scale(1.1);
    }
  }
`;
function ResearchCenterListComponent({
  data = null,
  researchcentersTagTree = null,
  pagination = false,
}) {
  const [listData, setListData] = useState<Array<any>>(
    () => data?.researchCenters || []
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPagesCount, setTotalPagesCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (researchcentersTagTree) {
      (async () => {
        try {
          setIsLoading(true);
          const apiService = new ApiService("fa", true);
          let params = {
            tagTrees: [researchcentersTagTree],
            orderBy: ["content.ResearchPriority:asc"],
          };
          if (pagination) {
            params = {
              ...params,
              size: SIZE,
              offset: 0,
              format: false,
            };
          }
          const response = await apiService.getContent(params, false);

          setTotalPagesCount(Math.ceil((response?.count || 0) / SIZE));
          const unsortedList = Formatter.formatData({ data: response });
          // setListData(unsortedList?.sort?.((a, b) => a?.['ResearchPriority']?.value - b?.['ResearchPriority']?.value) || []);
          setListData(unsortedList || []);
        } catch {
          toast.error("خطایی رخ داد");
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [researchcentersTagTree]);

  const researchCenters = useMemo(() => {
    const result = [];
    let clone = JSON.parse(JSON.stringify(listData));
    if (Array.isArray(clone)) {
      (function chunk() {
        result?.push?.(clone?.splice?.(0, 3));
        if (clone?.length > 0) return chunk();
      })();

      return result;
    }
    return [];
  }, [listData]);

  const researchCentersMobile = useMemo(() => {
    const result = [];
    let clone = JSON.parse(JSON.stringify(listData));
    if (Array.isArray(clone)) {
      (function chunk() {
        result.push(clone?.splice?.(0, 2));
        if (clone?.length > 0) return chunk();
      })();

      return result;
    }
    return [];
  }, [listData]);

  const handlePageClick = async (d) => {
    const selected = d?.selected;
    const apiService = new ApiService("fa", true);
    let params = {
      tagTrees: [researchcentersTagTree],
      orderBy: ["content.ResearchPriority:asc"],
    };
    if (pagination) {
      params = {
        ...params,
        size: SIZE,
        offset: selected * SIZE,
        format: false,
      };
    }
    try {
      setIsLoading(true);
      const response = await apiService.getContent(params, false);
      setCurrentPage(selected + 1);
      const unsortedList = Formatter.formatData({ data: response });
      // setListData(unsortedList?.sort?.((a, b) => a?.['ResearchPriority']?.value - b?.['ResearchPriority']?.value) || []);
      setListData(unsortedList || []);
    } catch {
      toast.error("خطایی رخ داد");
    } finally {
      setIsLoading(false);
    }
  };
  if (isLoading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center">
        <Loading />
      </div>
    );
  }
  return (
    <CommonContainer>
      <Toast />
      <>
        <div className="tw-hidden lg:tw-block tw-px-4 md:tw-px-0">
          {researchCenters?.length > 0 ? (
            researchCenters?.map?.((parent) => (
              <div
                className={clsx(
                  "tw-mb-[30px] last:tw-mb-0 tw-gap-[30px] tw-flex-wrap lg:tw-flex-nowrap",
                  {
                    "md:tw-inline-flex lg:tw-flex tw-justify-center": true,
                    "tw-mx-auto md:tw-px-[30px]": parent?.length === 2,
                  }
                )}
              >
                {parent?.length > 0 ? (
                  parent?.map?.((v) => (
                    <StyledNewsEventCard
                      v={v}
                      key={v?.id}
                      size={parent?.length === 1 ? "large" : "small"}
                    />
                  ))
                ) : (
                  <NoContent />
                )}
              </div>
            ))
          ) : (
            <NoContent />
          )}
        </div>
        <div className="tw-block lg:tw-hidden tw-px-4 md:tw-px-0">
          {researchCentersMobile?.length > 0 ? (
            researchCentersMobile?.map?.((parent) => (
              <div
                className={clsx("tw-mb-[30px] last:tw-mb-0 tw-gap-[30px]", {
                  "tw-flex tw-mx-auto lg:tw-px-[30px] tw-mx-auto":
                    parent?.length < 2,
                  "tw-grid tw-grid-cols-1 sm:tw-grid-cols-2":
                    parent?.length === 2,
                })}
              >
                {parent?.length > 0 ? (
                  parent?.map?.((v) => (
                    <StyledNewsEventCard
                      v={v}
                      key={v?.id}
                      single={parent?.length === 1}
                    />
                  ))
                ) : (
                  <NoContent />
                )}
              </div>
            ))
          ) : (
            <NoContent />
          )}
        </div>
        {pagination && (
          <div className="tw-mt-5">
            <Pagination
              currentPage={currentPage}
              handlePageClick={handlePageClick}
              totalPagesCount={totalPagesCount}
            />
          </div>
        )}
      </>
    </CommonContainer>
  );
}

export default ResearchCenterListComponent;
